import { useEffect, useState } from 'react';
import { App as KonstaApp } from 'konsta/react';
import type { Language } from '@harmony/shared';
import { api, ApiError, type City, type Me, type ProviderRegistration } from './api';
import { colorScheme, getAgentHint, getInitData, isIOS } from './telegram';
import { LanguageProvider } from './i18n';
import { CenterMessage } from './components/CenterMessage';
import { LanguageScreen } from './screens/LanguageScreen';
import { WelcomeScreen } from './screens/WelcomeScreen';
import { RoleScreen } from './screens/RoleScreen';
import { Shell } from './screens/Shell';
import { ClientRegistration } from './screens/ClientRegistration';
import { DeniedScreen, PendingScreen, ProviderWizard } from './screens/ProviderWizard';
import { NavProvider } from './nav';
import { ToastProvider } from './components/ui';

/** Where a Telegram button asked to go, e.g. ?tab=requests or ?chat=<id>. */
const params = new URLSearchParams(window.location.search);
const deepLink = { tab: params.get('tab'), chat: params.get('chat'), page: params.get('page') };

type State =
  | { kind: 'loading' }
  | { kind: 'outside-telegram' }
  | { kind: 'auth-failed' }
  | { kind: 'suspended' }
  | { kind: 'ready'; me: Me; agentCode: string | null };

export default function App() {
  const [state, setState] = useState<State>({ kind: 'loading' });
  const [welcomeSeen, setWelcomeSeen] = useState(false);
  const [scheme, setScheme] = useState(colorScheme());
  useEffect(() => {
    const on = (e: Event) => setScheme((e as CustomEvent<'light' | 'dark'>).detail);
    window.addEventListener('harmony:theme', on);
    return () => window.removeEventListener('harmony:theme', on);
  }, []);

  useEffect(() => {
    const initData = getInitData();
    if (!initData) return setState({ kind: 'outside-telegram' });
    const hint = getAgentHint();
    api
      .login(initData, hint.startParam)
      .then((r) => setState({ kind: 'ready', me: r.user, agentCode: hint.agent ?? r.agentCode }))
      .catch((e) => {
        console.error(e);
        setState({ kind: e instanceof ApiError && e.code === 'ACCOUNT_NOT_ACTIVE' ? 'suspended' : 'auth-failed' });
      });
  }, []);

  const lang: Language = state.kind === 'ready' ? state.me.language : 'en';
  const update = (me: Me) => setState((s) => (s.kind === 'ready' ? { ...s, me } : s));

  let screen;
  if (state.kind === 'loading') screen = <CenterMessage loading textKey="app.loading" />;
  else if (state.kind === 'outside-telegram') screen = <CenterMessage textKey="app.open_in_telegram" />;
  else if (state.kind === 'auth-failed') screen = <CenterMessage textKey="app.auth_failed" />;
  else if (state.kind === 'suspended') screen = <CenterMessage textKey="app.account_suspended" />;
  else if (!state.me.languageChosen) screen = <LanguageScreen onChosen={update} />;
  else if (!state.me.role && !welcomeSeen) screen = <WelcomeScreen onContinue={() => setWelcomeSeen(true)} />;
  // Someone arriving through an agent's link is almost certainly a provider; they still choose.
  else if (!state.me.role) screen = <RoleScreen onChosen={update} agentCode={state.agentCode} />;
  else screen = <RoleGate me={state.me} agentCode={state.agentCode} onMeChange={update} />;

  return (
    <KonstaApp theme={isIOS() ? 'ios' : 'material'} dark={scheme === 'dark'} safeAreas>
      <LanguageProvider lang={lang}>
        <ToastProvider>
          <NavProvider>{screen}</NavProvider>
        </ToastProvider>
      </LanguageProvider>
    </KonstaApp>
  );
}

/** Sends each role to registration, a status screen, or the main app. */
function RoleGate({ me, agentCode, onMeChange }: { me: Me; agentCode: string | null; onMeChange: (m: Me) => void }) {
  const [cities, setCities] = useState<City[] | null>(null);
  const [clientReady, setClientReady] = useState<boolean | null>(null);
  const [provider, setProvider] = useState<ProviderRegistration | null>(null);
  const [editing, setEditing] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        if (me.role === 'CLIENT') {
          const ok = await api
            .clientProfile()
            .then(() => true)
            .catch((e) => (e instanceof ApiError && e.status === 404 ? false : Promise.reject(e)));
          if (!ok) setCities(await api.cities());
          setClientReady(ok);
        } else if (me.role === 'PROVIDER') {
          const [reg, cs] = await Promise.all([api.providerRegistration(), api.cities()]);
          setProvider(reg);
          setCities(cs);
        }
      } catch (e) {
        console.error(e);
        setFailed(true);
      }
    };
    load();
  }, [me.role]);

  if (failed) return <CenterMessage textKey="error.generic" />;

  if (me.role === 'CLIENT') {
    if (clientReady === null) return <CenterMessage loading textKey="app.loading" />;
    if (!clientReady)
      return <ClientRegistration cities={cities ?? []} defaultName={me.firstName ?? ''} onDone={() => setClientReady(true)} />;
  }

  if (me.role === 'PROVIDER') {
    if (!provider || !cities) return <CenterMessage loading textKey="app.loading" />;
    if (provider.status === 'PENDING') return <PendingScreen />;
    if (provider.status === 'DENIED' && !editing)
      return <DeniedScreen reason={provider.denialReason} onReapply={() => setEditing(true)} />;
    if (provider.status !== 'APPROVED')
      return (
        <ProviderWizard
          initial={{ ...provider, draft: { displayName: me.firstName ?? undefined, ...provider.draft } }}
          cities={cities}
          agentHint={agentCode}
          onSubmitted={(r) => {
            setEditing(false);
            setProvider(r);
          }}
        />
      );
  }

  return <Shell me={me} onMeChange={onMeChange} deepLink={deepLink} />;
}
